import React from "react";
import { Building2, BarChart3, CircleCheck, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDashboardStatsQuery } from "@/hooks/queries";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: stats, isLoading: loading } = useDashboardStatsQuery();

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <Card
      className={cn(
        "h-full transition-all duration-200",
        onClick && "cursor-pointer hover:-translate-y-1 hover:shadow-md"
      )}
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="mb-3 flex items-center">
          <div
            className="mr-3 flex h-12 w-12 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${color}26`, color }}
          >
            {icon}
          </div>
          <span className="text-sm font-medium text-muted-foreground">{title}</span>
        </div>
        {loading ? (
          <Skeleton className="h-10 w-16" />
        ) : (
          <p className="text-3xl font-semibold tracking-tight">{value}</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div>
      <h1 className="mb-8 text-3xl font-semibold tracking-tight">Dashboard</h1>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Applications"
          value={stats?.totalBorrowers || 0}
          icon={<Building2 className="h-6 w-6" />}
          color="#1976d2"
          onClick={() => navigate("/applications")}
        />
        <StatCard
          title="Pending Decisions"
          value={stats?.pendingDecisions || 0}
          icon={<Clock className="h-6 w-6" />}
          color="#ed6c02"
          onClick={() => navigate("/underwriting")}
        />
        <StatCard
          title="Approved"
          value={stats?.approvedDecisions || 0}
          icon={<CircleCheck className="h-6 w-6" />}
          color="#2e7d32"
          onClick={() => navigate("/underwriting")}
        />
        <StatCard
          title="Approval Rate"
          value={
            stats
              ? `${((stats.approvedDecisions / (stats.approvedDecisions + stats.pendingDecisions)) * 100 || 0).toFixed(0)}%`
              : "0%"
          }
          icon={<BarChart3 className="h-6 w-6" />}
          color="#9c27b0"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-8">
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Recent Applications</h2>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-[60px] w-full rounded-md" />
                ))}
              </div>
            ) : stats?.recentBorrowers.length ? (
              <div className="space-y-2">
                {stats.recentBorrowers.map((borrower) => (
                  <button
                    key={borrower.id}
                    type="button"
                    className="w-full rounded-md bg-muted/60 p-4 text-left transition-colors hover:bg-muted"
                    onClick={() => navigate(`/applications/${borrower.id}`)}
                  >
                    <p className="font-medium">{borrower.company_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {borrower.owner_name} • {borrower.processing_status}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">
                No applications yet. Create your first application to get started.
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="lg:col-span-4">
          <CardContent className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
            <button
              type="button"
              className="mb-3 w-full rounded-lg bg-primary p-4 text-left text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={() => navigate("/applications/new")}
            >
              <p className="font-semibold">+ New Application</p>
              <p className="text-sm opacity-90">Create a new application</p>
            </button>
            <button
              type="button"
              className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-muted/50"
              onClick={() => navigate("/underwriting")}
            >
              <p className="font-semibold">View Decisions</p>
              <p className="text-sm text-muted-foreground">Check underwriting status</p>
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
